//NOTE: This widget's definition is included in powerSchoolDesignSystemToolkit.ts
import { BasicValidationRules } from 'common-utils/interfaces';
import { PdsAbstractInputWidget } from './pds-abstract-input-widget';
import { pdsValidationRulesEngine } from './pds-validation-rules-engine';
import { SelectOption } from './interfaces/select-option';
import { pdsLogger } from './pds-logger';
import { PdsSelectWidget as PdsSelectWidgetInterface } from './interfaces/pds-select-widget';
import { SelectOptionGroup } from './interfaces/select-option-group';
import { isArray } from 'util';

let log = pdsLogger.prependLogger(pdsLogger.requiresLogger('input-widgets'), 'pds-select-widget.ts');

export class PdsSelectWidget extends PdsAbstractInputWidget implements PdsSelectWidgetInterface {
  is = 'pds-select-widget';

  // default value is a copy of model value used when redrawing options. Must use a copy of model value to avoid Polymer binding issues
  options: SelectOption[] | SelectOptionGroup[];

  properties = {
    //AbstractInputWidget Properties
    name: String, //We construct the id using the name property
    dataLabelText: String,
    dataShowLabel: {
      type: String,
      value: null
    },
    dataBadgeText: {
      type: String,
      value: '',
      observer: 'setUpBadge'
    },
    dataHelperText: {
      type: String,
      value: null
    },
    dataTooltipText: String,
    dataUseAlternateColorScheme: {
      type: String,
      value: 'false'
    },
    dataIsreadonly: {
      type: String,
      value: 'false',
      observer: 'setReadonlyText'
    },
    dataIsfieldonly: {
      //This is for adding a class to get rid of the bottom padding.
      type: String,
      value: 'false'
    },
    dataHideUnelevatedBadge: {
      type: String,
      value: 'false',
      observer: 'setUpBadge'
    },
    dataDisableInvalidBadgeElevation: {
      type: String,
      value: 'false',
      observer: 'setUpBadge'
    },
    //Widget specific Validation Properties
    dataIsrequired: {
      type: String,
      value: 'false',
      observer: 'setUpBadge'
    },
    //Option to activate the validation indicator
    dataUseValidIndicator: {
      type: String,
      value: 'false'
    },
    dataCustomvalidate: String,
    disabled: {
      type: String,
      value: 'false'
    },
    options: {
      type: Array,
      observer: 'validateOptionsDeferred' // if the options change, the readonly text could potentially change
    }
  };

  private isGrouped = false;

  /**
   * Sets up validation rules needed for select widgets.
   */
  generateValidationRules(): void {
    let validationRules: BasicValidationRules = pdsValidationRulesEngine.getBasicValidationRules();
    validationRules.isText = true;
    validationRules.setProperty('isRequired', this.dataIsrequired, Boolean);

    this.validators = pdsValidationRulesEngine.getValidatorsForRules(validationRules);
    if ('dataCustomvalidate' in this) {
      this.validators.push({
        execute: this.pds.validation.getCustomValidate(this.dataCustomvalidate),
        condition: {
          element: this
        }
      });
    }
  }

  /**
   * Overrides the default to get the first child select element.
   */
  inputSelector(): any {
    return this.querySelector('select');
  }

  /**
   * Force the options list to repopulate.
   */
  redrawOptions(): void {
    log(`Redrawing the options for ${this.name}.`);

    this.redraw('options');

    // set the value back to the model value after the options have been rebuilt
    this.setValue(this.modelValue, true);
  }

  /**
   * @param modelValue - the new model value to apply to the select
   * @param skip - skips setting the value. (Keeps from getting into recursive loops)
   * @returns returns if the model value was actually set.
   */
  setModelValue(modelValue: string, skip = false): boolean {
    let select = this.inputSelector();

    if (select != null && modelValue !== select.modelValue) {
      select.modelValue = modelValue;

      log(`Setting ${this.name}'s model value from '${select.modelValue}' to '${modelValue}'.`);

      if (skip !== true) {
        this.setValue(modelValue, true);
      }

      return true;
    }

    return false;
  }

  /**
   * Sets the readonly text from option text corresponding to the current value.
   */
  setReadonlyText(): void {
    let readOnlyEle = this.querySelector('.pds-readonly-data');
    if (readOnlyEle && this.dataIsreadonly != null && this.options) {
      // clear the readonly text and move on if the widget is not readonly
      if (this.dataIsreadonly !== 'true') {
        readOnlyEle.innerText = '';
        return;
      }

      let selectedOption: SelectOption | undefined;

      if (this.isGrouped) {
        let groupedOptions = this.options as SelectOptionGroup[];
        selectedOption = groupedOptions.reduce((foundOption: SelectOption, optionGroup: SelectOptionGroup) => {
          return foundOption || optionGroup.value.find((o: SelectOption) => o.value === this.value);
        }, undefined);
      } else {
        let flatOptions = this.options as SelectOption[];
        selectedOption = flatOptions.find((o: SelectOption) => o.value === this.value);
      }

      if (selectedOption) {
        readOnlyEle.innerText = selectedOption.text;
        log(`Setting ${this.name}'s readonly only text to '${selectedOption.text}' from the value '${this.value}'.`);
      } else {
        log(`No option in ${this.name} was found corresponding to the value '${this.value}' when setting read only text.`);
        readOnlyEle.innerText = '';
      }
    }
  }

  /**
   * @param value - The string the user typed into the input.
   * @param skip - skips setting the model value. (Keeps from getting into recursive loops)
   */
  setValue(value: string, skip = false): void {
    let modelValue = value;

    // if the value being being set is null/undefined, set it to an empty string so the comparison with the current value will work
    if (value == null) {
      value = '';
    }

    let select = this.inputSelector();
    if (select != null && value !== select.value) {
      let oldValue = select.value;
      select.value = value;

      log(`Setting ${this.name}'s value from '${oldValue}' to '${select.value}'.`);
    }

    if (skip !== true) {
      this.setModelValue(modelValue, true);
    }
  }

  /**
   * Used to make sure options values have been set before validating options array.
   */
  validateOptionsDeferred(oldValue, newValue): void {
    if (oldValue == null && newValue == null) {
      return;
    }

    this.isGrouped = this.options != null && this.options.length > 0 && Array.isArray(this.options[0].value);

    setTimeout(() => {
      this.validateOptions();
    });
  }

  /**
   * Check for duplicate text or values in the options array.
   * @returns a list of duplicates.
   */
  validateOptions(): { duplicateText: string[], duplicateValues: string[] } {
    this.setReadonlyText();

    let duplicateText = this.getDuplicateProperties(this.options, 'text') as Array<string>;

    let duplicateValues = this.getDuplicateProperties(this.options, 'value') as Array<string>;

    if (!this.disableWarningMessageDuplicateOption && duplicateText && duplicateText.length) {
      let displayText = duplicateText
        // format any duplicates found for display
        .map((value: string) => `'${value}'`)
        .join(', ');
      console.warn(`Duplicate option text (${displayText}) was found in ${this.name}'s option list.`);
    }

    if (!this.disableWarningMessageDuplicateOption && duplicateValues && duplicateValues.length) {
      let displayValues = duplicateValues
        // format any duplicates found for display
        .map((text: string) => `'${text}'`)
        .join(', ');
      console.warn(`Duplicate option values (${displayValues}) were found in ${this.name}'s option list.`);
    }

    return {
      duplicateText,
      duplicateValues
    };
  }

  /**
   * Checks for duplicate property values in an array.
   * @param items: The array values to check for duplicates.
   * @param property: The property to check in each array item.
   * @returns an array of duplicate values
   */
  protected getDuplicateProperties(items: Array<any>, property: string): Array<any> {
    // if there are no items, there can be no duplicates
    if (!(items instanceof Array)) {
      return [];
    }

    // split the array into the first item and the rest - we will be checking if the first item is a duplicate
    let [item, ...otherItems] = items;

    // if there are no other items, this item cannot be a duplicate
    if (otherItems.length === 0) {
      return [];
    }

    // look for any other items that don't match the current item.
    let nonMatches = otherItems.filter((otherItem: any) => otherItem[property] !== item[property]);

    // this is a duplicate if any matches were found
    let thisDuplicate = nonMatches.length !== otherItems.length ? [item[property]] : [];

    // check for duplicate properties on the items that didn't match
    let duplicateProperties = this.getDuplicateProperties(nonMatches, property);

    // return this duplicate combined with any other duplicates
    return thisDuplicate.concat(duplicateProperties);
  }
}
